import React, { useState, useRef, useEffect } from 'react';
import { Plus, Mic, Send, Trash2 } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';
import { NoAutoFillInput } from '../ui/NoAutoFillInput';

export const MessageInput = ({
  inputValue,
  onInputChange,
  onSubmit,
  t,
  onSendVoice,
  onSendImage,
  onOpenMoments,
  onOpenChat
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [showWaveform, setShowWaveform] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [maxTime] = useState(90);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const fileInputRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isRecording && recordingBlob) {
      timerRef.current = setTimeout(() => {
        stopRecording();
      }, (maxTime - recordingTime) * 1000);
      return () => clearTimeout(timerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, recordingTime, maxTime, recordingBlob]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordingBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setShowWaveform(true);
      triggerHaptic();

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateWaveform = () => {
        if (!isRecording) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255);
        animationRef.current = requestAnimationFrame(updateWaveform);
      };
      updateWaveform();

      const timeTimer = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxTime) {
            clearInterval(timeTimer);
            return maxTime;
          }
          return prev + 1;
        });
      }, 1000);

      timerRef.current = timeTimer;

    } catch (err) {
      console.error('Failed to start recording:', err);
      alert('无法访问麦克风，请确保已授权麦克风权限');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      clearInterval(timerRef.current);
      setIsRecording(false);
      setShowWaveform(false);
      triggerHaptic();
    }
  };

  const handleSend = () => {
    if (recordingBlob) {
      onSendVoice?.(recordingBlob, recordingTime);
      setRecordingBlob(null);
      setRecordingTime(0);
      triggerHaptic();
    } else if (imagePreview) {
      onSendImage?.(imagePreview);
      setImagePreview(null);
      triggerHaptic();
    } else if (inputValue.trim()) {
      onSubmit?.(new Event('submit'));
    }
  };

  const deleteRecording = () => {
    setRecordingBlob(null);
    setRecordingTime(0);
    triggerHaptic();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('请上传图片文件');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const deleteImage = () => {
    setImagePreview(null);
    triggerHaptic();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 px-4 pt-2 z-40" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}>
      {(onOpenMoments || onOpenChat) && (
        <div className="max-w-md mx-auto mb-2 flex gap-2">
          {onOpenChat && (
            <button
              onClick={onOpenChat}
              className="glass-card rounded-[32px] px-4 py-2 flex items-center gap-2 hover:bg-white/5 transition-colors"
            >
              <span className="text-white text-sm font-medium">chat</span>
            </button>
          )}
          {onOpenMoments && (
            <button
              onClick={onOpenMoments}
              className="glass-card rounded-[32px] px-4 py-2 flex items-center gap-2 hover:bg-white/5 transition-colors"
            >
              <span className="text-white text-sm font-medium">moments</span>
            </button>
          )}
        </div>
      )}
      <div className="max-w-md mx-auto glass-card rounded-[32px] p-2 flex items-center gap-3 transition-all duration-300">
        {!recordingBlob && !imagePreview && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-12 h-12 rounded-full flex items-center justify-center text-gray-400 hover:text-[var(--neon-primary)] hover:bg-white/10 transition-all flex-shrink-0"
            >
              <Plus size={26} strokeWidth={2} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            <NoAutoFillInput
              type="text"
              inputMode="text"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder={t.typeConnect}
              name="message-input"
              id="message-input"
              className="flex-1 bg-transparent border-none outline-none text-white h-12 min-h-[48px] py-2"
            />

            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                isRecording
                  ? 'bg-red-500/80 text-white animate-pulse'
                  : 'text-gray-400 hover:text-[var(--neon-primary)]'
              }`}
            >
              {isRecording ? (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              ) : (
                <Mic size={22} strokeWidth={1.5} />
              )}
            </button>

            <button
              type="button"
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                inputValue.trim()
                  ? 'bg-[var(--neon-primary)] text-black'
                  : 'bg-white/10 text-gray-500'
              }`}
              onClickCapture={() => {
                if (inputValue.trim()) triggerHaptic();
              }}
            >
              <Send size={22} strokeWidth={1.5} />
            </button>
          </>
        )}

        {recordingBlob && (
          <>
            <div className="flex-1 flex items-center gap-3 px-2">
              <div className="flex-1 h-12 rounded-xl bg-gradient-to-r from-red-500/20 to-red-600/10 border border-red-500/30 flex items-center px-4 gap-3">
                <div className="flex items-center gap-1 h-8">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-red-500 rounded-full transition-all duration-75"
                      style={{
                        height: `${Math.max(8, audioLevel * 48)}px`,
                        opacity: showWaveform ? 1 : 0.3
                      }}
                    ></div>
                  ))}
                </div>
                <span className="text-sm font-mono text-red-400">{formatTime(recordingTime)}</span>
              </div>

              <button
                type="button"
                onClick={deleteRecording}
                className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/40 transition-all"
              >
                <Trash2 size={18} strokeWidth={1.5} />
              </button>

              <button
                type="button"
                onClick={handleSend}
                className="w-12 h-12 rounded-full bg-[var(--neon-primary)] text-black flex items-center justify-center hover:opacity-90 transition-all"
              >
                <Send size={22} strokeWidth={1.5} />
              </button>
            </div>
          </>
        )}

        {imagePreview && (
          <>
            <div className="flex-1 flex items-center gap-3 px-2">
              <div className="relative h-12 rounded-xl bg-white/5 border border-white/10 flex items-center px-2 overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-10 w-auto rounded-lg object-cover"
                />
                <button
                  type="button"
                  onClick={deleteImage}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-red-500 transition-all"
                >
                  <Trash2 size={12} strokeWidth={2} />
                </button>
              </div>

              <button
                type="button"
                onClick={deleteImage}
                className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/40 transition-all"
              >
                <Trash2 size={18} strokeWidth={1.5} />
              </button>

              <button
                type="button"
                onClick={handleSend}
                className="w-12 h-12 rounded-full bg-[var(--neon-primary)] text-black flex items-center justify-center hover:opacity-90 transition-all"
              >
                <Send size={22} strokeWidth={1.5} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
